import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Users, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Phone, Mail, MapPin, TrendingUp, DollarSign, FileText, Building, List, LayoutList, Shield, UserCheck, Clock, Briefcase } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { CustomDatePicker } from '../../shared/components/custom';
import '../../core/styles/global-dropdowns.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatPointageDate, formatPointageDateWithHijri, ASSIGNMENT_STATUS_VALUES, ASSIGNMENT_TYPE_VALUES, TEAM_ROLE_VALUES, normalizeAssignmentStatus, normalizeAssignmentType, normalizeTeamRole, getSaudiWorkCityOptions, getSaudiWorkCityLabel } from './pointageHelpers';

const AffectationsEquipesPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.affectationsEquipesPage.${key}`, opts);

  const getStatusLabel = (status) => {
    const normalized = normalizeAssignmentStatus(status);
    const map = {
      [ASSIGNMENT_STATUS_VALUES.active]: p('status_active'),
      [ASSIGNMENT_STATUS_VALUES.suspended]: p('status_suspended'),
      [ASSIGNMENT_STATUS_VALUES.ended]: p('status_ended'),
      [ASSIGNMENT_STATUS_VALUES.planned]: p('status_planned'),
    };
    return map[normalized] || p('em_dash');
  };

  const getAssignmentTypeLabel = (value) => {
    const normalized = normalizeAssignmentType(value);
    const map = {
      [ASSIGNMENT_TYPE_VALUES.permanent]: p('type_permanent'),
      [ASSIGNMENT_TYPE_VALUES.temporary]: p('type_temporary'),
      [ASSIGNMENT_TYPE_VALUES.mission]: p('type_mission'),
    };
    return map[normalized] || value || p('em_dash');
  };

  const getRoleLabel = (value) => {
    const normalized = normalizeTeamRole(value);
    const map = {
      [TEAM_ROLE_VALUES.teamLead]: p('role_team_lead'),
      [TEAM_ROLE_VALUES.member]: p('role_member'),
      [TEAM_ROLE_VALUES.substitute]: p('role_substitute'),
      [TEAM_ROLE_VALUES.intern]: p('role_intern'),
    };
    return map[normalized] || value || p('em_dash');
  };

  const workZoneOptions = getSaudiWorkCityOptions();
  const getWorkZoneLabel = (value) => getSaudiWorkCityLabel(value);

  const [searchTerm, setSearchTerm] = useState('');
  const [affectations, setAffectations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const AFFECTATION_FORM_FIELD_COUNT = 11;

  const affectationFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_employee_info'), icon: UserCheck, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_zone_notes'), icon: MapPin, iconClass: 'text-red-400' },
    ],
  }, AFFECTATION_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    employee_id: '',
    employee_name: '',
    team_name: '',
    role_in_team: '',
    schedule_profile: '',
    work_zone: '',
    assignment_start_date: '',
    assignment_end_date: '',
    assignment_type: '',
    status: '',
    responsible_manager: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_affectations');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadAffectations(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAffectations = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/affectations-equipes');
      const rows = response?.data || response || [];
      setAffectations(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading affectations:', error);
      setAffectations([]);
    }
  };

  const stats = {
    total: affectations.length,
    active: affectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.active).length,
    teams: new Set(affectations.map(a => a.team_name)).size,
    employees: new Set(affectations.map((a) => a.employee_id || a.employee_name)).size,
    occupation: affectations.length > 0 ? ((affectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.active).length / affectations.length) * 100).toFixed(1) : 0
  };

  const mainTabs = [
    { id: 'all_affectations', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_equipe', name: p('tab_by_team'), icon: Users },
    { id: 'par_zone', name: p('tab_by_zone'), icon: MapPin }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: ASSIGNMENT_STATUS_VALUES.active, name: p('filter_active'), icon: CheckCircle },
    { id: ASSIGNMENT_TYPE_VALUES.temporary, name: p('filter_temporary'), icon: Clock },
    { id: ASSIGNMENT_TYPE_VALUES.permanent, name: p('filter_permanent'), icon: Shield },
    { id: ASSIGNMENT_STATUS_VALUES.planned, name: p('filter_planned'), icon: Calendar }
  ];

  const getTeamDistribution = () => {
    const teams = {};
    affectations.forEach(a => {
      const teamKey = a.team_name || 'no_team';
      teams[teamKey] = (teams[teamKey] || 0) + 1;
    });
    return Object.entries(teams).slice(0, 4).map(([key, count], idx) => ({
      filterKey: key,
      name: key === 'no_team' ? p('no_team') : key,
      count,
      color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-orange-500 to-orange-600'][idx]
    }));
  };

  const getZoneDistribution = () => {
    const zones = {};
    affectations.forEach(a => {
      const zoneKey = a.work_zone || 'no_zone';
      zones[zoneKey] = (zones[zoneKey] || 0) + 1;
    });
    return Object.entries(zones).slice(0, 4).map(([key, count], idx) => ({
      filterKey: key,
      name: key === 'no_zone' ? p('no_zone') : getWorkZoneLabel(key),
      count,
      color: ['bg-gradient-to-r from-cyan-500 to-cyan-600', 'bg-gradient-to-r from-pink-500 to-pink-600', 'bg-gradient-to-r from-yellow-500 to-yellow-600', 'bg-gradient-to-r from-indigo-500 to-indigo-600'][idx]
    }));
  };

  const affectationDistribution = {
    all_affectations: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'suspended', name: p('dist_suspended'), count: affectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.suspended).length, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'ended', name: p('dist_ended'), count: affectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.ended).length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { filterKey: 'planned', name: p('dist_planned'), count: affectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.planned).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_equipe: {
      title: p('dist_by_team_title'),
      data: getTeamDistribution()
    },
    par_zone: {
      title: p('dist_by_zone_title'),
      data: getZoneDistribution()
    }
  };

  const distributionInfo = affectationDistribution[mainViewMode] || affectationDistribution.all_affectations;

  
  const baseFilteredAffectations = useMemo(() => affectations.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    
    if (dateRange.start || dateRange.end) {
      const startDate = new Date(a.assignment_start_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (startDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (startDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !a.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !a.team_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !a.work_zone?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_affectations' && quickFilter !== 'all') {
      if (quickFilter === ASSIGNMENT_STATUS_VALUES.active && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.active) return false;
      if (quickFilter === ASSIGNMENT_STATUS_VALUES.planned && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.planned) return false;
      if (quickFilter === ASSIGNMENT_TYPE_VALUES.temporary && normalizeAssignmentType(a.assignment_type) !== ASSIGNMENT_TYPE_VALUES.temporary) return false;
      if (quickFilter === ASSIGNMENT_TYPE_VALUES.permanent && normalizeAssignmentType(a.assignment_type) !== ASSIGNMENT_TYPE_VALUES.permanent) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_affectations':
          if (activeDistributionFilter === 'active' && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.active) return false;
          if (activeDistributionFilter === 'suspended' && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.suspended) return false;
          if (activeDistributionFilter === 'ended' && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.ended) return false;
          if (activeDistributionFilter === 'planned' && normalizeAssignmentStatus(a.status) !== ASSIGNMENT_STATUS_VALUES.planned) return false;
          break;
        case 'par_equipe':
          if (activeDistributionFilter !== (a.team_name || 'no_team')) return false;
          break;
        case 'par_zone':
          if (activeDistributionFilter !== (a.work_zone || 'no_zone')) return false;
          break;
      }
    }
    return true;
  }), [affectations, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(() => [
      textColumnRule('employee_name', (a) => a.employee_name || ''),
      textColumnRule('team_name', (a) => a.team_name || ''),
      textColumnRule('role_in_team', (a) => a.role_in_team || ''),
      textColumnRule('assignment_start_date', (a) => formatPointageDate(a.assignment_start_date) || ''),
      textColumnRule('schedule', (a) => a.schedule_name || a.schedule || ''),
      textColumnRule('zone', (a) => a.zone_name || a.zone || ''),
      textColumnRule('assignment_end_date', (a) => formatPointageDate(a.assignment_end_date) || ''),
      exactColumnRule('status', (a) => normalizeAssignmentStatus(a.status)),
    ], []);

  const filteredAffectations = useMemo(
    () => applyColumnFilters(baseFilteredAffectations, colFilters, columnFilterRules),
    [baseFilteredAffectations, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        employee_id: formData.employee_id,
        employee_name: formData.employee_name
          || resolvePointageEmployeeDisplayName(formData.employee_id, employeeOptions, ''),
      };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/affectations-equipes/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/affectations-equipes', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadAffectations();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/affectations-equipes/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadAffectations();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/affectations-equipes/${id}`, { status: newStatus });
      loadAffectations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/affectations-equipes/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadAffectations();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      employee_id: '', employee_name: '', team_name: '', role_in_team: '', schedule_profile: '', work_zone: '',
      assignment_start_date: '', assignment_end_date: '', assignment_type: '', status: '',
      responsible_manager: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredAffectations);


  const POINTAGE_TABLE_COLUMNS = [
    { key: 'employee_name', labelKey: 'col_employee' },
    { key: 'team_name', labelKey: 'col_team' },
    { key: 'role_in_team', labelKey: 'col_role' },
    { key: 'schedule', labelKey: 'col_schedule' },
    { key: 'zone', labelKey: 'col_zone' },
    { key: 'assignment_start_date', labelKey: 'col_start_date' },
    { key: 'assignment_end_date', labelKey: 'col_end_date' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'actions', required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadAffectations} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_assignment')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_teams'), value: stats.teams, icon: Shield, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_employees'), value: stats.employees, icon: UserCheck, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_occupation'), value: `${stats.occupation}%`, icon: TrendingUp, color: 'from-cyan-500 to-cyan-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_affectations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_total', { pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('employee_name') && (
                    <TableColumnHeaderFilter columnKey="employee_name" label={p('col_employee')} value={colFilters.employee_name ?? ''} onChange={(v) => setColFilter('employee_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('team_name') && (
                    <TableColumnHeaderFilter columnKey="team_name" label={p('col_team')} value={colFilters.team_name ?? ''} onChange={(v) => setColFilter('team_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('role_in_team') && (
                    <TableColumnHeaderFilter columnKey="role_in_team" label={p('col_role')} value={colFilters.role_in_team ?? ''} onChange={(v) => setColFilter('role_in_team', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('schedule') && (
                    <TableColumnHeaderFilter columnKey="schedule" label={p('col_schedule')} value={colFilters.schedule ?? ''} onChange={(v) => setColFilter('schedule', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('zone') && (
                    <TableColumnHeaderFilter columnKey="zone" label={p('col_zone')} value={colFilters.zone ?? ''} onChange={(v) => setColFilter('zone', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('assignment_start_date') && (
                    <TableColumnHeaderFilter columnKey="assignment_start_date" label={p('col_start_date')} value={colFilters.assignment_start_date ?? ''} onChange={(v) => setColFilter('assignment_start_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('assignment_end_date') && (
                    <TableColumnHeaderFilter columnKey="assignment_end_date" label={p('col_end_date')} value={colFilters.assignment_end_date ?? ''} onChange={(v) => setColFilter('assignment_end_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAffectations.length > 0 ? (
                  filteredAffectations.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('employee_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{resolvePointageEmployeeDisplayName(record.employee_id, employeeOptions, record.employee_name)}</span></td>
                      )}
                      {isColVisible('team_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.team_name}</span></td>
                      )}
                      {isColVisible('role_in_team') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getRoleLabel(record.role_in_team)}</span></td>
                      )}
                      {isColVisible('schedule') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.schedule_profile || '-'}</span></td>
                      )}
                      {isColVisible('zone') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getWorkZoneLabel(record.work_zone) || '-'}</span></td>
                      )}
                      {isColVisible('assignment_start_date') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.assignment_start_date ? formatPointageDate(record.assignment_start_date) : '-'}</span></td>
                      )}
                      {isColVisible('assignment_end_date') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.assignment_end_date ? formatPointageDate(record.assignment_end_date) : '-'}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={normalizeAssignmentStatus(record.status)}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status')}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Users size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredAffectations.length })}</span>
                      <span>{p('footer_active')} <span className="text-white font-semibold">{filteredAffectations.filter(a => normalizeAssignmentStatus(a.status) === ASSIGNMENT_STATUS_VALUES.active).length}</span></span>
                      <span>{p('footer_teams')} <span className="text-white font-semibold">{new Set(filteredAffectations.map(a => a.team_name)).size}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredAffectations.length > 0 ? (
              filteredAffectations.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Users size={20} className="text-emerald-400" />{record.employee_name}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_employee')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.employee_name}</div>
                            <div><span className="font-medium">{p('label_team')}</span> {record.team_name}</div>
                            <div><span className="font-medium">{p('label_role')}</span> {getRoleLabel(record.role_in_team)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_schedule')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_schedule')}</span> {record.schedule_profile || p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_zone')}</span> {record.work_zone || p('em_dash')}</div>
                            <div><span className="font-medium">{p('type')}</span> {getAssignmentTypeLabel(record.assignment_type)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start')}</span> {record.assignment_start_date ? formatPointageDate(record.assignment_start_date) : p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_end')}</span> {record.assignment_end_date ? formatPointageDate(record.assignment_end_date) : p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_status')}</span> {getStatusBadge(record.status)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_manager')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_manager')}</span> {record.responsible_manager || p('em_dash')}</div>
                            {record.notes && <div className="mt-2 p-2 bg-white/5 rounded"><span className="font-medium text-white/80 text-xs">{p('label_notes')}</span><p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Users size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="affectations-equipes-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><Users size={18} className="text-emerald-400" />{p('view_section_employee')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('view_employee')}</p><p className={modalForm.viewValue}>{viewingRecord.employee_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_team')}</p><p className={modalForm.viewValue}>{viewingRecord.team_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_role')}</p><p className={modalForm.viewValue}>{getRoleLabel(viewingRecord.role_in_team)}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_period')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_schedule')}</p><p className={modalForm.viewValue}>{viewingRecord.schedule_profile || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_start_date')}</p><p className={modalForm.viewValue}>{viewingRecord.assignment_start_date ? formatPointageDate(viewingRecord.assignment_start_date) : p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_end_date')}</p><p className={modalForm.viewValue}>{viewingRecord.assignment_end_date ? formatPointageDate(viewingRecord.assignment_end_date) : p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('type')}</p><p className={modalForm.viewValue}>{getAssignmentTypeLabel(viewingRecord.assignment_type)}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_zone')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_zone')}</p><p className={modalForm.viewValue}>{viewingRecord.work_zone || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_manager')}</p><p className={modalForm.viewValue}>{viewingRecord.responsible_manager || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                      {viewingRecord.notes && <div><p className={modalForm.viewLabel}>{p('field_notes')}</p><p className={modalForm.viewValue}>{viewingRecord.notes}</p></div>}
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Users}
          onSubmit
          formId="affectation-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="affectations-equipes-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={affectationFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="affectation-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(affectationFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_employee')} />}</label><CustomSelect value={resolvePointageEmployeeSelectValue(formData, employeeOptions)} onChange={(value) => { const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(value, employeeOptions); setFormData({ ...formData, employee_id: employeeId, employee_name: employeeName }); }} options={employeeOptions} searchable={true} creatable={false} autoSelect={false} placeholder={p('ph_employee')} /></div>
                <div><label className={modalForm.label}>{p('field_team')}</label><CustomSelect value={formData.team_name} onChange={(value) => setFormData({ ...formData, team_name: value })} options={[]} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_team')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_role')}</label><CustomSelect value={formData.role_in_team} onChange={(value) => setFormData({ ...formData, role_in_team: value })} options={roleOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_select_role')} /></div>
                <div><label className={modalForm.label}>{p('field_assignment_type')}</label><CustomSelect value={formData.assignment_type} onChange={(value) => setFormData({ ...formData, assignment_type: value })} options={assignmentTypeOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_select_type')} /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(affectationFormTabs, formTab, 'details') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_start_date')}</label><CustomDatePicker value={formData.assignment_start_date} onChange={(value) => setFormData({ ...formData, assignment_start_date: value })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{p('field_end_date')}</label><CustomDatePicker value={formData.assignment_end_date} onChange={(value) => setFormData({ ...formData, assignment_end_date: value })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{p('field_schedule_profile')}</label><CustomSelect value={formData.schedule_profile} onChange={(value) => setFormData({ ...formData, schedule_profile: value })} options={[]} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_schedule_profile')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_work_zone')}</label><CustomSelect value={formData.work_zone} onChange={(value) => setFormData({ ...formData, work_zone: value })} options={workZoneOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_work_zone')} /></div>
                <div><label className={modalForm.label}>{p('field_manager')}</label><CustomSelect value={formData.responsible_manager} onChange={(value) => setFormData({ ...formData, responsible_manager: value })} options={[]} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_manager')} /></div>
                <div><label className={modalForm.label}>{p('field_status')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={formStatusOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_select_status')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} /></div>
              </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default AffectationsEquipesPage;

